// components/Purchase.tsx
import { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { faFilter, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import PurchaseTable from 'components/devices/PurchaseTable';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import PhoenixLoader from 'components/common/PhoenixLoader';
import { fetchApprovedRequests } from 'store/approvalsSlice';
import { fetchCompanies } from 'store/companySlice';

const Purchase = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Get auth state
  const authState = useSelector((state: RootState) => state.auth);
  const companyId = authState?.companyId;
  const role = authState?.role;

  // Check if user is super admin (companyId is null for super admin)
  const isSuperAdmin =
    companyId === null ||
    companyId === undefined ||
    (role && typeof role === 'string' && role.toLowerCase().includes('super'));

  // FIX: Get data from APPROVALS slice, not assets slice
  const approvalsState = useSelector((state: RootState) => state.approvals);
  const approvedRequests = approvalsState.approved; // This is the correct data
  const loading = approvalsState.loading;
  const error = approvalsState.error;

  // Get companies data
  const companies = useSelector((state: RootState) => state.companies.data);

  // Fetch companies for filter dropdown
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, isSuperAdmin]);

  // Fetch approved requests when component mounts
  useEffect(() => {
    if (isSuperAdmin) {
      // For now, let's use 0 for super admin since API expects a number
      dispatch(fetchApprovedRequests({ company_id: 0, skip: 0, limit: 100 }));
    } else if (companyId) {
      // For regular users, convert companyId string to number
      const company_id = parseInt(companyId);
      if (company_id > 0) {
        dispatch(fetchApprovedRequests({ company_id, skip: 0, limit: 100 }));
      }
    }
  }, [dispatch, companyId, isSuperAdmin, role]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  // Filter approved requests based on search query and selected company
  const filteredRequests = useMemo(() => {
    return approvedRequests.filter(request => {
      const query = searchQuery.toLowerCase();

      // Filter by company if selected
      if (
        selectedCompanyId !== null &&
        request.company_id !== selectedCompanyId
      ) {
        return false;
      }

      // Filter by priority
      if (
        selectedPriority &&
        request.priority?.toLowerCase() !== selectedPriority.toLowerCase()
      ) {
        return false;
      }

      // Filter by status
      if (
        selectedStatus &&
        request.status?.toLowerCase() !== selectedStatus.toLowerCase()
      ) {
        return false;
      }

      // Filter by search query
      if (!query) return true;

      return (
        (request.user_name || '').toLowerCase().includes(query) ||
        (request.asset_name || '').toLowerCase().includes(query) ||
        (request.category_name || '').toLowerCase().includes(query) ||
        (request.request_note || '').toLowerCase().includes(query) ||
        request.company_name?.toLowerCase().includes(query)
      );
    });
  }, [
    approvedRequests,
    searchQuery,
    selectedCompanyId,
    selectedPriority,
    selectedStatus
  ]);

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon
            icon={faShoppingCart}
            className="me-2 text-primary"
          />
          Approved Purchase Requests
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search approved requests..."
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2 flex-wrap">
              {isSuperAdmin && companies.length > 0 && (
                <CompanyFilter
                  companies={companies}
                  selectedCompanyId={selectedCompanyId}
                  onCompanySelect={handleCompanySelect}
                />
              )}
              <select
                className="form-select form-select-sm"
                value={selectedPriority || ''}
                onChange={e => setSelectedPriority(e.target.value || null)}
                style={{ width: 'auto', minWidth: '150px' }}
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <select
                className="form-select form-select-sm"
                value={selectedStatus || ''}
                onChange={e => setSelectedStatus(e.target.value || null)}
                style={{ width: 'auto', minWidth: '150px' }}
              >
                <option value="">All Status</option>
                <option value="approved">Approved</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="on-hold">On Hold</option>
              </select>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedCompanyId(null);
                  setSelectedPriority(null);
                  setSelectedStatus(null);
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        <div className="mt-3">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-4">
              <PhoenixLoader />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-danger" role="alert">
              Error loading purchases: {error}
            </div>
          )}

          {/* No Company Found (only for non-super admins) */}
          {!isSuperAdmin && !companyId && !loading && (
            <div className="text-center p-4">
              <p>Unable to load company information. Please log in again.</p>
            </div>
          )}

          {/* No Requests Found Message */}
          {!loading && !error && filteredRequests.length === 0 && (
            <div className="text-center py-4">
              <h5>No Approved Purchase Requests Found</h5>
              <p className="text-muted">
                {isSuperAdmin
                  ? 'There are no approved purchase requests across all companies.'
                  : 'There are no approved purchase requests in your company.'}
              </p>
            </div>
          )}

          {/* Purchase Table - Shows APPROVED requests */}
          {!loading && !error && filteredRequests.length > 0 && (
            <PurchaseTable
              company_id={
                isSuperAdmin ? 0 : companyId ? parseInt(companyId) : 0
              }
              requests={filteredRequests}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Purchase;
